import {
  InlineEditForm,
  type InlineEditFormHandle,
  type InlineEditSubmitHelpers,
} from "@birdwatch/ui/form";
import { cn } from "cn";
import { useImperativeHandle, useRef } from "react";
import { z } from "zod";
import { ShowMoreText } from "@/components/ui/common/show-more-text";
import { Markdown } from "@/components/ui/markdown";
import { Typography } from "@/components/ui/typography";
import { RichTextField } from "@/fields/rich-text/rich-text-field";
import { RichTextSkeleton } from "@/fields/rich-text/rich-text-skeleton";

const FIELD = "value";
const defaultValueSchema = z.string().trim();

/** The single-field shape `InlineEditForm` is instantiated with. */
type InlineRichTextValues = { [FIELD]: string };

/** Opens the editor from outside the view-mode trigger. */
export type InlineRichTextHandle = {
  /** Enter edit mode with `value` in the editor — unsaved until the user commits (blur saves, Escape discards). */
  edit: (value: string) => void;
  /** The markdown as it stands: the open editor's unsaved text, else the saved value. */
  read: () => string;
};

export type InlineRichTextProps = {
  /** Stored markdown. Also the edit baseline and the view-mode content. */
  value: string;
  /**
   * Persist. Throw to keep the editor open; call `helpers.setFieldError` to
   * show a server message inline instead of a toast.
   */
  onSave: (value: string, helpers: InlineEditSubmitHelpers) => Promise<void>;
  /** Editor placeholder AND the italic empty-state hint in view mode. */
  placeholder: string;
  /** Accessible name for the edit trigger, e.g. "Edit description". */
  editLabel: string;
  /**
   * Validation for the markdown string. Default `z.string().trim()`.
   * Both type args are pinned: a bare `z.ZodType<string>` has an `unknown`
   * INPUT, which makes the composed object schema unassignable to the form's
   * `FormValidateOrFn<{ value: string }>`.
   */
  schema?: z.ZodType<string, string>;
  /**
   * Shows an `n/max` count of the trimmed markdown at the right of the open
   * editor's formatting toolbar. Display only: `schema` enforces the limit.
   */
  maxLength?: number;
  disabled?: boolean;
  /** A new value is on its way (an AI draft): a skeleton stands in for the view, and the editor cannot be opened. */
  loading?: boolean;
  /** Collapse height of the rendered markdown. Default 200. */
  maxHeight?: number;
  /** View-mode container class; merged after the layout defaults. */
  className?: string;
  /** Class on the rendered `Markdown`. Default `"text-sm"`. */
  markdownClassName?: string;
  "data-test"?: string;
  /** Told whenever the editor opens or closes. */
  onOpenChange?: (open: boolean) => void;
  ref?: React.Ref<InlineRichTextHandle>;
};

export function InlineRichText({
  value,
  onSave,
  placeholder,
  editLabel,
  schema = defaultValueSchema,
  maxLength,
  disabled,
  loading,
  maxHeight = 200,
  className,
  markdownClassName = "text-sm",
  "data-test": dataTest,
  onOpenChange,
  ref,
}: InlineRichTextProps) {
  const formRef = useRef<InlineEditFormHandle<InlineRichTextValues>>(null);
  useImperativeHandle(
    ref,
    () => ({
      edit: (next) => formRef.current?.edit({ [FIELD]: next }),
      read: () => formRef.current?.read()[FIELD] ?? value,
    }),
    [value],
  );

  return (
    <InlineEditForm<InlineRichTextValues>
      ref={formRef}
      onOpenChange={onOpenChange}
      defaultValues={{ [FIELD]: value ?? "" }}
      schema={z.object({ [FIELD]: schema })}
      editLabel={editLabel}
      disabled={disabled || loading}
      onSubmit={async (values, helpers) => onSave(values[FIELD], helpers)}
      displayValue={
        <div
          data-test={dataTest}
          className="w-full"
          onClick={(e) => {
            // Let markdown anchors navigate without entering edit mode.
            if ((e.target as HTMLElement).nodeName === "A") e.stopPropagation();
          }}
        >
          {loading ? (
            <RichTextSkeleton />
          ) : (
            <ShowMoreText maxHeight={maxHeight}>
              {value ? (
                <Markdown content={value} className={markdownClassName} />
              ) : (
                <Typography size="sm" className="text-text-dark-low italic">
                  {placeholder}
                </Typography>
              )}
            </ShowMoreText>
          )}
        </div>
      }
      // block + min-h-0 override InlineEditForm's flex / items-center / min-h-10
      // so multi-paragraph markdown lays out naturally.
      className={cn(/* sorted */ "block", "min-h-0", "-mx-2 p-2", className)}
    >
      {(field) => (
        <RichTextField
          field={field}
          autoFocus
          placeholder={placeholder}
          emptyEditorClass="italic"
          inputClassName="rounded-2xl"
          contentClassName="max-h-none"
          toolbarEnd={
            maxLength !== undefined && (
              <span className="text-xs text-text-dark-low tabular-nums">
                {field.state.value.trim().length}/{maxLength}
              </span>
            )
          }
        />
      )}
    </InlineEditForm>
  );
}
